import { escape } from 'lodash-es';

import {
  tokenizeString,
  generateRegionsOfChange,
  Region,
  ComparableRegionsOfChange,
} from './tokenizers';

// 10 lines * 75 tokens (roughly indicative of a long code line) per line.
export const WORD_DIFF_TOKEN_LIMIT = 750;
const DELETED_MARKER = 'del';
const ADDED_MARKER = 'ins';

export type TextDiff = {
  previousText: string;
  newText: string;
};

export function addMarkers(
  regions: Region[],
  rawText: string,
  tag: string
): string {
  // Note: this function overlaps in functionality with emphasizeMatches. See
  // "matches emphasizeMatches output across newlines" test in
  // word-diff.test.tsx. Consider removing the duplication if possible.
  const tagStart = `<${tag}>`;
  const tagEnd = `</${tag}>`;

  if (regions.length) {
    const [newText, finalEnd] = regions.reduce(
      ([prevText, prevEnd]: [string, number], region) => {
        // Here we are adding the start and end tags on each line.
        // This is a typical use case for using a .split() and a .join(), but we
        // can't do that here. We need to preserve indentation and have the tags
        // only wrap the non-whitespace content of each line.
        // To handle that, we split on newlines PLUS indentation, and preserve
        // that in the split array. Then we iterate over the array and manually
        // add the right tags. Since the newlines and indentation are already in
        // the array, we can join on an empty string to arrive at our desired
        // output.
        const taggedText = rawText
          .substring(region.start, region.end)
          .split(/(\n\s*)/)
          .map(line => {
            if (/\n/.test(line)) {
              return line;
            }
            return tagStart + escape(line) + tagEnd;
          })
          .join('');

        return [
          prevText +
            escape(rawText.substring(prevEnd, region.start)) +
            taggedText,
          region.end,
        ];
      },
      ['', 0]
    );

    return newText + escape(rawText.substring(finalEnd as number));
  }
  return escape(rawText);
}

export function wordDiffRegions(
  before: string,
  after: string
): ComparableRegionsOfChange {
  const beforeTokens = tokenizeString(before);
  const afterTokens = tokenizeString(after);
  if (
    beforeTokens.length > WORD_DIFF_TOKEN_LIMIT ||
    afterTokens.length > WORD_DIFF_TOKEN_LIMIT
  ) {
    return { originalRegions: [], revisedRegions: [] };
  }
  const { originalRegions, revisedRegions } = generateRegionsOfChange(
    beforeTokens,
    afterTokens
  );
  return { originalRegions, revisedRegions };
}

/**
 * Finds the word differences between each before/after pair of strings in the
 * input array.
 *
 * @param textDiffs The before and after versions of the source
 * @returns An array of regions that can be used to mark up the source. Each
 * element in the resulting array corresponds to the TextDiff object in the same
 * position in the input list.
 */
export function produceWordDiffRegions(
  textDiffs: TextDiff[]
): ComparableRegionsOfChange[] {
  return textDiffs.map(textDiff => {
    return wordDiffRegions(textDiff.previousText, textDiff.newText);
  });
}

/**
 * Returns a new array of before/after strings, with <ins> and <del> tags added
 * to represent the word differences between those strings. This is a
 * convenience function to combine produceWordDiffRegions and
 * produceWordDiffsFromRegions.
 *
 * @param textDiffs The original before and after versions of the source
 * @returns The before and after versions of the source with word diff markup
 */
export function produceWordDiffs(textDiffs: TextDiff[]): TextDiff[] {
  const regions = produceWordDiffRegions(textDiffs);
  return produceWordDiffsFromRegions(textDiffs, regions);
}

/**
 * Marks up source using the regions specified.
 * @param textDiffs The before and after versions of the source
 * @param regions Regions representing the parts of the source to be marked up.
 * Each object in this array should correspond to the object at the
 * corresponding position in the textDiffs array.
 * @returns The before and after versions of the source with word diff markup
 */
export function produceWordDiffsFromRegions(
  textDiffs: TextDiff[],
  regions: ComparableRegionsOfChange[]
): TextDiff[] {
  return textDiffs.map((textDiff, idx) => {
    const { previousText, newText } = textDiff;
    const { originalRegions, revisedRegions } = regions[idx];
    return {
      previousText: addMarkers(originalRegions, previousText, DELETED_MARKER),
      newText: addMarkers(revisedRegions, newText, ADDED_MARKER),
    };
  });
}
